/**
 * Orders API Contract Schemas - WAVE B STRICT REWRITE
 *
 * Defines Zod schemas for orders domain API contracts:
 * - RAW schemas: Backend response format (snake_case) - validated before normalization
 * - Normalized schemas: Frontend model format (camelCase) - after normalization
 *
 * Flow: Backend (snake_case) → RAW Schema validation → Normalizer → Frontend (camelCase)
 *
 * RAW-first model: All client endpoints MUST have RAW schemas wired for contract validation.
 *
 * WAVE B CHANGES:
 * - Removed all `.passthrough()` calls - schemas are now strict
 * - Made `result` fields required for operation endpoints (no more optional())
 * - Replaced UnknownRecord/UnknownArray with endpoint-specific schemas
 * - Split success/error envelopes for polymorphic endpoints
 * - Removed permissive fallback unions from GetOneOrderResponseSchema
 *
 * @module api/contracts/orders
 */

import { z } from "zod"

// ============================================================================
// Base Primitives
// ============================================================================

/**
 * Order item ID - database integer ID (returned as string by PHP mysqli)
 * @deprecated Unused - kept for potential future use
 */
// eslint-disable-next-line no-unused-vars
const _OrderItemIdSchema = z.string()

/**
 * User ID - database integer ID (returned as string by PHP mysqli)
 * @deprecated Unused - kept for potential future use
 */
// eslint-disable-next-line no-unused-vars
const _UserIdSchema = z.string()

/**
 * Manager ID from dictionary (returned as string by PHP mysqli)
 * @deprecated Unused - kept for potential future use
 */
// eslint-disable-next-line no-unused-vars
const _ManagerIdSchema = z.string()

/**
 * Payment type ID (returned as string by PHP mysqli)
 */
// eslint-disable-next-line no-unused-vars
const PaymentTypeIdSchema = z.string()

/**
 * Delivery type ID (returned as string by PHP mysqli)
 * @deprecated Unused - kept for potential future use
 */
// eslint-disable-next-line no-unused-vars
const _DeliveryTypeIdSchema = z.string()

/**
 * Order status string
 */
const OrderStatusSchema = z.string()

/**
 * User email
 * @deprecated Unused - kept for potential future use
 */
// eslint-disable-next-line no-unused-vars
const _EmailSchema = z.string().email()

/**
 * Generic message string
 */
const MessageSchema = z.string()

// ----------------------------------------------------------------------------
// Dashboard market-orders widget payload (RAW)
// Backend returns market-ready rows with derived fields consumed by OrderRow.vue
// ----------------------------------------------------------------------------

/**
 * RAW dashboard list row for market orders.
 *
 * @precondition Backend already enriched marketplace rows for the dashboard list
 * seam and exposed derived `products_qty` and `store_price` fields at the top level.
 * @postcondition Successful parses preserve the backend-authored market row while
 * requiring the derived fields the frontend widget renders directly.
 * @throws ZodError when the market dashboard payload loses `products_qty` or
 * `store_price`.
 */
export const DashboardMarketOrderRowSchema = z
  .object({
    m_item_id: z.union([z.number(), z.string()]),
    m_item_date_created: z.union([z.number(), z.string()]),
    m_item_sum: z.union([z.number(), z.string()]),
    m_item_admin_comment: z.union([z.string(), z.null()]),
    m_item_market: z.string(),
    m_item_market_id: z.union([z.string(), z.null()]),
    m_item_market_ready: z.union([z.number(), z.string(), z.null()]),
    m_item_market_shipment: z.union([z.string(), z.null()]),
    m_item_market_status: z.union([z.string(), z.null()]),
    products_qty: z.union([z.number(), z.string()]),
    store_price: z.union([z.number(), z.string()]),
  })
  .passthrough()

/**
 * RAW dashboard market-orders response envelope.
 *
 * @precondition Backend returns the dashboard-ready market list under `data`
 * with pagination count in `allOrdersQty`.
 * @postcondition Successful parses preserve the response envelope while pinning
 * market-row derived fields to backend authority.
 * @throws ZodError when the list envelope stops returning market-ready rows.
 */
export const DashboardMarketOrdersResponseSchema = z
  .object({
    data: z.array(DashboardMarketOrderRowSchema),
    allOrdersQty: z.union([z.number(), z.string()]),
    status: z.union([z.number(), z.string()]).optional(),
  })
  .strict()

// ============================================================================
// Group 1: RAW Response Schemas (snake_case - what backend actually returns)
// These schemas validate the RAW response BEFORE normalization
// ============================================================================

/**
 * Operation result enumeration
 * Backend returns these literal values for operation status
 */
const OperationResultSchema = z.union([
  z.literal("ok"),
  z.literal("error"),
  z.literal("success"),
])

/**
 * Operation result with numeric success (some endpoints return 1)
 */
const OperationResultNumericSchema = z.union([
  OperationResultSchema,
  z.literal(1),
])

// ----------------------------------------------------------------------------
// update/order_params endpoint
// Backend returns: { result: "ok"|"error"|"success", message?: string }
// ----------------------------------------------------------------------------

/**
 * RAW schema for update/order_params endpoint
 * Operation result is REQUIRED for status semantics
 */
export const UpdateOrderParamsResponseSchema = z
  .object({
    result: OperationResultSchema,
    message: z.string().optional(),
  })
  .strict()

// ----------------------------------------------------------------------------
// update/order_manager endpoint
// Backend returns: { result: "ok"|"error"|"success" }
// ----------------------------------------------------------------------------

/**
 * RAW schema for update/order_manager endpoint
 * Operation result is REQUIRED for status semantics
 */
export const AssignManagerResponseSchema = z
  .object({
    result: OperationResultSchema,
  })
  .strict()

// ----------------------------------------------------------------------------
// custom/get_client_message endpoint
// Backend returns: { message: string } or null
// ----------------------------------------------------------------------------

/**
 * RAW schema for custom/get_client_message endpoint
 * Schema accepts both object and null - normalizer handles transformation
 * message is REQUIRED when object is returned
 */
const MessageTemplateObjectSchema = z
  .object({
    message: MessageSchema,
  })
  .strict()

export const MessageTemplateResponseSchema = z.union([
  MessageTemplateObjectSchema,
  z.null(),
])

// ----------------------------------------------------------------------------
// custom/send_client_message endpoint
// Backend returns: { result: "ok"|"error"|"success" }
// ----------------------------------------------------------------------------

/**
 * RAW schema for custom/send_client_message endpoint
 * Operation result is REQUIRED for status semantics
 */
export const SendMessageResponseSchema = z
  .object({
    result: OperationResultSchema,
  })
  .strict()

// ----------------------------------------------------------------------------
// send_order_tracking endpoint
// Backend returns: { errors?: string } (no errors = success)
// Split into success/error envelopes for clarity
// ----------------------------------------------------------------------------

/**
 * Success envelope for send_order_tracking endpoint
 * No errors field means success
 */
const AddTrackingSuccessSchema = z
  .object({
    errors: z.undefined(),
  })
  .strict()

/**
 * Error envelope for send_order_tracking endpoint
 */
const AddTrackingErrorSchema = z
  .object({
    errors: z.string(),
  })
  .strict()

/**
 * RAW schema for send_order_tracking endpoint
 * Polymorphic by presence of errors field
 */
export const AddTrackingNumberResponseSchema = z.union([
  AddTrackingSuccessSchema,
  AddTrackingErrorSchema,
])

// ----------------------------------------------------------------------------
// sdek/print/{orderId}/{sdekOrderId} endpoint
// Backend returns: { url?: string, wait?: string, print_id?: string }
// At least one of url or wait should be present
// ----------------------------------------------------------------------------

/**
 * RAW schema for sdek/print endpoint
 * Returns either a ready print URL or a pending wait state with optional print task id
 */
export const PrintCDEKLabelResponseSchema = z
  .object({
    url: z.string().optional(),
    wait: z.string().optional(),
    print_id: z.string().optional(),
  })
  .strict()
  .refine((data) => data.url || data.wait, {
    message: "At least one of 'url' or 'wait' must be present",
  })

// ----------------------------------------------------------------------------
// order_world_delivery_price endpoint
// Backend returns: { price: number } for quote calculation only
// ----------------------------------------------------------------------------

/**
 * RAW schema for order_world_delivery_price quote endpoint
 * Accepts country + weight and returns calculated quote only.
 */
export const DeliveryPriceQuoteResponseSchema = z
  .object({
    price: z.number(),
  })
  .strict()

// ----------------------------------------------------------------------------
// orders/change_delivery_price endpoint
// Backend returns: { status: 1 }
// ----------------------------------------------------------------------------

/**
 * RAW schema for orders/change_delivery_price save endpoint
 * Persists m_item_delivery_price for an existing order.
 */
export const DeliveryPriceSaveResponseSchema = z
  .object({
    status: z.number(),
  })
  .strict()

// ----------------------------------------------------------------------------
// custom/save_more_products_for_order endpoint
// Backend returns: { result: "ok"|"error"|"success"|1 }
// ----------------------------------------------------------------------------

/**
 * RAW schema for custom/save_more_products_for_order endpoint
 * Operation result is REQUIRED for status semantics
 * Can be string or numeric (1)
 */
export const AddProductResponseSchema = z
  .object({
    status: z.union([z.number(), z.string()]).optional(),
    result: OperationResultNumericSchema,
    new_sum: z.union([z.number(), z.string()]).optional(),
    new_full_sum: z.union([z.number(), z.string()]).optional(),
    delivery_price: z.union([z.number(), z.string()]).optional(),
    products_added: z.union([z.number(), z.string()]).optional(),
  })
  .strict()

// ----------------------------------------------------------------------------
// custom/order_cancel_promocode endpoint
// Backend returns: { result: "ok"|"error"|"success" }
// ----------------------------------------------------------------------------

/**
 * RAW schema for custom/order_cancel_promocode endpoint
 * Operation result is REQUIRED for status semantics
 */
export const CancelPromocodeResponseSchema = z
  .object({
    result: OperationResultSchema,
  })
  .strict()

// ----------------------------------------------------------------------------
// custom/order_save_ready endpoint
// Backend returns: { result: "ok"|"error"|"success" }
// ----------------------------------------------------------------------------

/**
 * RAW schema for custom/order_save_ready endpoint
 * Operation result is REQUIRED for status semantics
 */
export const SaveOrderReadyStatusResponseSchema = z
  .object({
    result: OperationResultSchema,
  })
  .strict()

// ----------------------------------------------------------------------------
// custom/save_order_oracle_response endpoint
// Backend returns: { result: 1|"ok"|"error"|"success" }
// ----------------------------------------------------------------------------

/**
 * RAW schema for custom/save_order_oracle_response endpoint
 * Operation result is REQUIRED for status semantics
 * Can be string or numeric (1)
 */
export const OracleRecommendationsResponseSchema = z
  .object({
    result: OperationResultNumericSchema,
  })
  .strict()

// ============================================================================
// Group 3: Order Product Schema (RAW)
// ============================================================================

// ============================================================================
// Group 4: Orders/search/report-adjacent endpoints (RAW schemas)
// ============================================================================

// ----------------------------------------------------------------------------
// custom/carts/{id} endpoint
// Backend returns: { id?: number, products?: [...], user_email?: string, ... }
// ----------------------------------------------------------------------------

/**
 * RAW schema for custom/carts/{id} endpoint
 * Cart details with products and user info
 */
const CartDetailProductSchema = z
  .object({
    id: z.union([z.number(), z.string()]),
    qty: z.union([z.number(), z.string()]),
    price: z.union([z.number(), z.string()]),
    name: z.string().optional(),
    options: z.record(z.unknown()).optional(),
    rowid: z.string().optional(),
    subtotal: z.union([z.number(), z.string()]).optional(),
  })
  .passthrough()

export const CustomCartByIdResponseSchema = z
  .object({
    id: z.union([z.number(), z.string()]).optional(),
    products: z
      .union([
        z.array(CartDetailProductSchema),
        z.record(CartDetailProductSchema),
      ])
      .optional(),
    user_email: z.union([z.string(), z.null()]).optional(),
    clientStat: z.union([z.boolean(), z.record(z.unknown())]).optional(),
  })
  .passthrough()

// ----------------------------------------------------------------------------
// get_one_order endpoint - PRIMARY FORMAT
// Backend returns: { order_info: {...}, products?: [...], sdek_order_id?, ... }
// ----------------------------------------------------------------------------

/**
 * RAW order info schema
 * Core order information from backend /api/get_one_order/:id payload.
 * Strict shape based on observed live backend responses.
 */
const OrderInfoSchema = z
  .object({
    m_item_id: z.number(),
    m_item_author: z.union([z.number(), z.null()]),
    m_item_visible: z.union([z.number(), z.null()]),
    m_item_date_created: z.union([z.number(), z.null()]),
    m_item_date_updated: z.union([z.number(), z.null()]),
    m_item_order_num: z.union([z.number(), z.null()]),
    m_item_title: z.union([z.string(), z.null()]),
    m_item_sum: z.number(),
    m_item_status: z.number(),
    m_item_user: z.union([z.number(), z.null()]),
    m_item_products: z.string(),
    m_item_payment_type: z.number(),
    m_item_delivery_type: z.number(),
    m_item_paid: z.number(),
    m_item_delivered: z.union([z.number(), z.null()]),
    m_item_tracking_number: z.union([z.string(), z.null()]),
    m_item_client_name: z.union([z.string(), z.null()]),
    m_item_client_phone: z.union([z.string(), z.null()]),
    m_item_client_email: z.union([z.string(), z.null()]),
    m_item_delivery_ins: z.union([z.number(), z.null()]),
    m_item_delivery_comment: z.union([z.string(), z.null()]),
    m_item_for_gift: z.number(),
    m_item_for_gift_comment: z.string(),
    m_item_country: z.number(),
    m_item_postcode: z.union([z.string(), z.null()]),
    m_item_city: z.union([z.string(), z.null()]),
    m_item_region: z.union([z.string(), z.null()]),
    m_item_address: z.union([z.string(), z.null()]),
    m_item_delivery_price: z.number(),
    m_item_admin_comment: z.union([z.string(), z.null()]),
    m_item_discount_fixed: z.union([z.number(), z.null()]),
    m_item_discount_percent: z.union([z.number(), z.null()]),
    m_item_oracle_manager: z.number(),
    m_item_oracle_time: z.union([z.number(), z.null()]),
    m_item_prev_id: z.union([z.number(), z.null()]),
    m_item_delivery_pvz_address: z.union([z.string(), z.null()]),
    m_item_points: z.union([z.number(), z.null()]),
    m_item_paid_date: z.union([z.number(), z.null()]),
    m_item_with_bertus: z.union([z.number(), z.null()]),
    m_item_bertus_delivery_price: z.union([z.number(), z.null()]),
    m_item_address_used: z.union([z.number(), z.null()]),
    m_item_bertus_delivery_type: z.union([z.number(), z.null()]),
    m_item_with_sales: z.union([z.number(), z.null()]),
    m_item_cert_sum: z.number(),
    m_item_cert_purchase_sum: z.number(),
    m_item_cert_num: z.union([z.number(), z.null()]),
    m_item_cert_discount: z.union([z.number(), z.null()]),
    m_item_reserved: z.union([z.number(), z.null()]),
    m_item_package_status: z.union([z.number(), z.null()]),
    m_item_yasplit_checkout_url: z.union([z.string(), z.null()]),
    m_item_sum_no_discount: z.union([z.number(), z.null()]),
    m_item_promocode_name: z.union([z.string(), z.null()]),
    m_item_url_ref: z.union([z.string(), z.null()]),
    m_item_manager: z.union([z.number(), z.null()]),
    m_item_market: z.string(),
    m_item_market_id: z.union([z.string(), z.null()]),
    m_item_market_ready: z.union([z.number(), z.null()]),
    m_item_market_shipment: z.union([z.string(), z.null()]),
    m_item_market_status: z.union([z.string(), z.null()]),
    m_item_delivery_pvz_id: z.union([z.string(), z.null()]),
    m_item_delivery_more_data: z.string(),
    m_item_tg_name: z.union([z.string(), z.null()]),
    m_item_cert_text: z.union([z.string(), z.null()]),
    m_item_insales_order_id: z.union([z.string(), z.null()]),
    m_item_market_fbo: z.number(),
    m_item_feedback_check: z.boolean(),
    m_item_custom: z.union([z.number(), z.null()]),
    m_item_client_type: z.string(),
    f_dir: z.string(),
    user_stat: z.union([z.boolean(), z.record(z.unknown())]),
  })
  .strict()

const MeaningfulOrderRowIdSchema = z.union([
  z.string().refine((value) => value.trim() !== ""),
  z.number().refine((value) => Number.isSafeInteger(value) && value >= 0),
])

const GetOneOrderProductItemSchema = z
  .object({
    id: z.union([z.number(), z.string()]),
    qty: z.union([z.number(), z.string()]),
    price: z.union([z.number(), z.string()]),
    name: z.string().optional(),
    options: z.record(z.unknown()).optional(),
    rowid: MeaningfulOrderRowIdSchema.optional(),
    subtotal: z.union([z.number(), z.string()]).optional(),
    supplier: z.string().optional(),
    stock: z.string().optional(),
    stock_2: z.number().nullable().optional(),
    prev_num: z.number().nullable().optional(),
    copy_left: z.number().nullable().optional(),
    copy_num: z.union([z.number(), z.string()]).nullable().optional(),
    sale_manager: z.number().nullable().optional(),
  })
  .passthrough()

/**
 * RAW schema for get_one_order endpoint - PRIMARY FORMAT
 * Standard response with order_info and optional products
 */
const GetOneOrderPrimarySchema = z
  .object({
    order_info: OrderInfoSchema,
    products: z.union([
      z.record(GetOneOrderProductItemSchema),
      z.array(GetOneOrderProductItemSchema),
    ]),
    rec_products: z.boolean(),
    sdek_order_id: z.string(),
    sdek_order_print_url: z.union([z.string(), z.null()]),
    // Print task UUID is absent until SDEK accepts the first print generation request.
    // RAW contract therefore allows null on trusted dev before the task is enqueued.
    sdek_order_print_id: z.union([z.string(), z.null()]),
  })
  .strict()

/**
 * RAW schema for get_one_order endpoint - LEGACY ARRAY FORMAT
 * Legacy backend returns array with single element containing order_info
 * KEPT for backward compatibility - verified by tests
 */
const GetOneOrderLegacyArraySchema = z.array(GetOneOrderPrimarySchema)

/**
 * RAW schema for get_one_order endpoint
 * Supports both primary object and legacy array formats
 * REMOVED UnknownRecordSchema fallback - now strict
 */
export const GetOneOrderResponseSchema = z.union([
  GetOneOrderPrimarySchema,
  GetOneOrderLegacyArraySchema,
])

// ----------------------------------------------------------------------------
// orders/{id} endpoint (fallback for get_one_order)
// Backend returns: { order_info: {...}, ... }
// ----------------------------------------------------------------------------

/**
 * RAW schema for orders/{id} endpoint
 * Used as fallback when get_one_order fails
 */
export const OrdersByIdResponseSchema = z
  .object({
    order_info: OrderInfoSchema,
    m_item_status: OrderStatusSchema.optional(),
  })
  .strict()

const OrdersByIdWrappedResponseSchema = z
  .object({
    status: z.number(),
    data: z
      .object({
        m_item_id: z.number(),
      })
      .passthrough(),
  })
  .strict()

export const OrdersByIdAnyResponseSchema = z.union([
  OrdersByIdResponseSchema,
  OrdersByIdWrappedResponseSchema,
])

// ----------------------------------------------------------------------------
// orders/positions endpoint
// Backend returns: array of order products
// ----------------------------------------------------------------------------

const OrdersPositionItemSchema = z
  .object({
    id: z.union([z.number(), z.string()]),
    qty: z.union([z.number(), z.string()]),
    price: z.union([z.number(), z.string()]),
    name: z.string().optional(),
    options: z.record(z.unknown()).optional(),
    rowid: MeaningfulOrderRowIdSchema.optional(),
    subtotal: z.union([z.number(), z.string()]).optional(),
  })
  .passthrough()

/**
 * RAW schema for orders/positions endpoint
 * Backend returns envelope: { status: 1, data: [...], items: [...], order: {...} }
 */
export const OrdersPositionsResponseSchema = z
  .object({
    status: z.number(),
    data: z.array(OrdersPositionItemSchema),
    items: z.array(OrdersPositionItemSchema),
    order: z.record(z.unknown()),
  })
  .strict()

// ----------------------------------------------------------------------------
// modules/search_items endpoint
// Backend returns: array of module items
// ----------------------------------------------------------------------------

/**
 * RAW schema for modules/search_items endpoint
 * Module search results
 */
export const ModulesSearchItemsItemSchema = z
  .object({
    id: z.string(),
    title: z.string().optional(),
    visible: z.boolean().optional(),
  })
  .strict()

export const ModulesSearchItemsResponseSchema = z.array(
  ModulesSearchItemsItemSchema
)

// ----------------------------------------------------------------------------
// get_shop_visits_closed endpoint
// Backend returns: array of timestamps or { dates: [...] }
// ----------------------------------------------------------------------------

/**
 * RAW schema for get_shop_visits_closed endpoint
 * Returns either array of timestamps or object with dates array
 */
export const GetShopVisitsClosedResponseSchema = z.union([
  z.array(z.number().int().positive()),
  z
    .object({
      dates: z.array(z.number().int().positive()),
    })
    .strict(),
])

// ----------------------------------------------------------------------------
// custom/search_product endpoint
// Backend returns: array of products
// ----------------------------------------------------------------------------

/**
 * RAW schema for custom/search_product endpoint
 * Product search results
 */
const CustomSearchProductItemSchema = z
  .object({
    id: z.string().optional(),
    m_item_id: z.string().optional(),
    product_type: z.string().optional(),
    m_item_title: z.string().optional(),
  })
  .strict()

export const CustomSearchProductResponseSchema = z.array(
  CustomSearchProductItemSchema
)

// ----------------------------------------------------------------------------
// custom/search_client endpoint
// Backend returns: array of clients
// ----------------------------------------------------------------------------

/**
 * RAW schema for custom/search_client endpoint
 * Client search results
 */
const CustomSearchClientItemSchema = z
  .object({
    user_id: z.string().optional(),
    user_email: z.string().optional(),
  })
  .strict()

export const CustomSearchClientResponseSchema = z.array(
  CustomSearchClientItemSchema
)

// ----------------------------------------------------------------------------
// rcmd_data endpoint
// Backend returns: { data: {...} }
// ----------------------------------------------------------------------------

/**
 * RAW schema for rcmd_data endpoint
 * Recommendation data with nested structure
 */
/**
 * RAW schema for rcmd_data.
 *
 * @precondition Backend returns the legacy recommendation envelope with a
 * top-level `status` flag and an object-shaped `data` payload.
 * @postcondition Successful parses preserve the raw nested recommendation data
 * so smoke tests and callers can inspect backend keys without coercion.
 * @throws ZodError when the endpoint stops returning the legacy envelope.
 */
export const RcmdDataResponseSchema = z
  .object({
    status: z.union([z.number(), z.string()]).optional(),
    data: z.record(z.unknown()),
  })
  .passthrough()

/**
 * RAW schema for store-pickup report responses.
 *
 * @precondition Backend returns either a successful report payload (array or
 * object envelope) or the structured `error` envelope emitted by the legacy
 * report controller.
 * @postcondition Successful parses preserve whichever raw backend shape was
 * returned so smoke tests can distinguish real backend blockers from stale test
 * wiring.
 * @throws ZodError when the backend response is neither a recognized success
 * shape nor the structured error envelope.
 */
export const StorePickupReportResponseSchema = z.union([
  z.array(z.record(z.unknown())),
  z
    .object({
      rows: z.array(z.record(z.unknown())).optional(),
      data: z
        .union([z.array(z.record(z.unknown())), z.record(z.unknown())])
        .optional(),
      lastDate: z.union([z.number(), z.string(), z.null()]).optional(),
    })
    .passthrough(),
  z
    .object({
      error: z
        .object({
          code: z.number(),
          message: z.string(),
          details: z.string().optional(),
        })
        .passthrough(),
    })
    .strict(),
])

// ----------------------------------------------------------------------------
// custom/discogs endpoint
// Backend returns: { lp: {...} } with status + lp union, or { status, errors: [...] }
// RAW Discogs payload includes tracklist, year_edition, album, artist, country, label
// Supports both legacy (tracklist/album/year_edition) and new (m_item_tracklist/m_item_title/m_item_year) field names
// ----------------------------------------------------------------------------

/**
 * RAW schema for Discogs artist data
 * Multiple shapes: object with m_item_id/m_item_title (new), object with id/title (old), or string (error)
 */
const DiscogsArtistSchema = z.union([
  z
    .object({
      m_item_id: z.union([z.number(), z.string()]),
      m_item_title: z.string(),
    })
    .strict(),
  z
    .object({
      id: z.union([z.number(), z.string()]),
      title: z.string(),
    })
    .strict(),
  z.string(),
])

/**
 * RAW schema for Discogs country data
 * Multiple shapes: object with m_item_id/m_item_title (new), object with id/title (old), or string (error/raw)
 */
const DiscogsCountrySchema = z.union([
  z
    .object({
      m_item_id: z.union([z.number(), z.string()]),
      m_item_title: z.string(),
    })
    .strict(),
  z
    .object({
      id: z.union([z.number(), z.string()]),
      title: z.string(),
    })
    .strict(),
  z.string(),
])

/**
 * RAW schema for Discogs label data
 * Multiple shapes: object with m_item_id/m_item_title (new), object with id/title (old), or string (error)
 */
const DiscogsLabelSchema = z.union([
  z
    .object({
      m_item_id: z.union([z.number(), z.string()]),
      m_item_title: z.string(),
    })
    .strict(),
  z
    .object({
      id: z.union([z.number(), z.string()]),
      title: z.string(),
    })
    .strict(),
  z.string(),
])

/**
 * RAW schema for custom/discogs endpoint - SUCCESS envelope
 * Discogs data in lp field with tracklist and optional album/year/artist/country/label
 * Supports both legacy and new field naming conventions
 */
const CustomDiscogsLpSchema = z
  .object({
    // Tracklist (both naming conventions supported)
    tracklist: z.string().optional(),
    m_item_tracklist: z.string().optional(),
    // Year (both naming conventions supported)
    year_edition: z.string().optional(),
    m_item_year: z.string().optional(),
    // Album title (both naming conventions supported)
    album: z.string().optional(),
    m_item_title: z.string().optional(),
    // Artist (multiple shapes: new object, old object, or string)
    artist: DiscogsArtistSchema.optional(),
    // Country (multiple shapes: new object, old object, or string)
    country: DiscogsCountrySchema.optional(),
    m_item_country: DiscogsCountrySchema.optional(),
    // Label (multiple shapes: new object, old object, or string)
    label: DiscogsLabelSchema.optional(),
  })
  .strict()
  .passthrough()

const MissingDiscogsDictionarySchema = z
  .object({
    dictionary: z.string(),
    title: z.string(),
  })
  .strict()

const CustomDiscogsSuccessSchema = z
  .object({
    status: z.number(),
    lp: CustomDiscogsLpSchema,
  })
  .strict()

/**
 * RAW schema for custom/discogs endpoint - ERROR envelope
 */
const CustomDiscogsErrorSchema = z
  .object({
    status: z.number(),
    errors: z.array(z.string()),
    missing_dictionaries: z
      .object({
        artist: MissingDiscogsDictionarySchema.optional(),
        label: MissingDiscogsDictionarySchema.optional(),
      })
      .strict()
      .optional(),
    lp: CustomDiscogsLpSchema.optional(),
  })
  .strict()

/**
 * RAW schema for custom/discogs endpoint
 * Polymorphic by presence of lp field (success) or errors field (error)
 */
export const CustomDiscogsResponseSchema = z.union([
  CustomDiscogsSuccessSchema,
  CustomDiscogsErrorSchema,
])

// ----------------------------------------------------------------------------
// custom/product_images endpoint
// Backend returns: { title: string, gallery: [...] }
// ----------------------------------------------------------------------------

/**
 * RAW schema for custom/product_images endpoint
 * Product images with gallery URLs
 */
export const CustomProductImagesResponseSchema = z
  .object({
    title: z.string(),
    gallery: z.array(z.string()),
  })
  .strict()
